import axios, { type AxiosResponse } from "axios";
import axiosConfig from "@/config/axios";
import type { OfertasEndpointMap } from "@/types/ofertas";

const buildHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...axiosConfig.headersOfertas,
});

const url = (path: string) => `${axiosConfig.ofertasURL}${path}`;

function httpPost<Res = unknown, Req = unknown>(
  path: string,
  payload: Req,
  token: string,
): Promise<AxiosResponse<Res>> {
  return axios.post<Res>(url(path), payload, { headers: buildHeaders(token) });
}

function httpGet<Res = unknown>(path: string, token: string): Promise<AxiosResponse<Res>> {
  return axios.get<Res>(url(path), { headers: buildHeaders(token) });
}

function postUnwrap<Res = unknown, Req = unknown>(
  path: string,
  payload: Req,
  token: string,
): Promise<Res> {
  return httpPost<Res, Req>(path, payload, token).then((r) => r.data);
}

function getUnwrap<Res = unknown>(path: string, token: string): Promise<Res> {
  return httpGet<Res>(path, token).then((r) => r.data);
}

/**
 * Dispatcher genérico usado por los stores. Cuando el endpoint está registrado
 * en `OfertasEndpointMap` (`@/types/ofertas`) el payload y la respuesta se
 * infieren automáticamente; para endpoints no tipados todavía, cae al overload
 * genérico con `<Res, Req>` manual.
 */
function dispatch<K extends keyof OfertasEndpointMap>(
  endpoint: K,
  payload: OfertasEndpointMap[K]["payload"],
  token: string,
): Promise<AxiosResponse<OfertasEndpointMap[K]["response"]>>;
function dispatch<Res = unknown, Req = unknown>(
  endpoint: string,
  payload: Req,
  token: string,
): Promise<AxiosResponse<Res>>;
function dispatch(endpoint: string, payload: unknown, token: string) {
  return httpPost(`${endpoint}/`, payload, token);
}

export default {
  loadInfo: (cliente: string, token: string) =>
    httpGet(`LoadInfoIndonet/${cliente}`, token),

  saveInfo: (valorPantalla: number | string, cliente: string, token: string) =>
    httpPost(
      "SaveInfoIndonetMonitorInfo/",
      { ValorPantalla: valorPantalla, Cliente: cliente },
      token,
    ),

  saveLogo: (cliente: string, content: string, token: string) =>
    httpPost("SaveInfoIndonetLogo/", { Cliente: cliente, Content: content }, token),

  getFotocromaticos: (org: string, cliente: string, token: string) =>
    httpPost("GetFotocromaticos/", { Vkorg: org, Cliente: cliente }, token),

  getTaladrosCliente: (cliente: string, token: string) =>
    httpGet(`GetTaladrosCliente/${cliente}`, token),

  get: dispatch,

  getLentraDetailsForCNXT: <Res = unknown, Req = unknown>(payload: Req, token: string) =>
    httpPost<Res, Req>("GetLentraDetailsForCNXT", payload, token),

  getDescripcionTratamiento: (
    VKORG: string,
    CodigoTratamiento: string,
    CodigoCaract: string,
    CarAdiccional: string,
    token: string,
  ) =>
    postUnwrap<string>(
      "GetDescripcionTratamiento/",
      {
        VKORG: VKORG || "",
        CodigoTratamiento: CodigoTratamiento || "",
        CodigoCaract: CodigoCaract || "",
        CarAdiccional: CarAdiccional || "",
      },
      token,
    ),

  getCaracteristicasLente: (VKORG: string, Cliente: string, Lente: string, token: string) =>
    postUnwrap(
      "GetCaracteristicasLente/",
      { VKORG: VKORG || "", Cliente: Cliente || "", Lente: Lente || "" },
      token,
    ),

  getTienePosicionUso: (VKORG: string, Lente: string, token: string) =>
    postUnwrap("TienePosicionUso/", { VKORG: VKORG || "", Lente: Lente || "" }, token),

  getDescripcionBasePrisma: (
    VKORG: string,
    CodigoBasePrisma: string,
    CarAdiccional: string,
    token: string,
  ) =>
    postUnwrap(
      "GetDescripcionBasePrisma/",
      {
        VKORG: VKORG || "",
        CodigoBasePrisma: CodigoBasePrisma || "",
        CarAdiccional: CarAdiccional || "",
      },
      token,
    ),

  getBases: (VKORG: string, token: string) =>
    getUnwrap(`GetBasesPorOrganizacion/${VKORG}`, token),

  getSuplementos: (Jerga: string, Vkorg: string, Stock: boolean, token: string) =>
    postUnwrap(
      "GetSuplementos/",
      { Vkorg: Vkorg || "", Jerga: Jerga || "", Stock: Stock || false },
      token,
    ),
};
